import { useEffect, useRef, useState } from "react";
import { useInView, animate } from "framer-motion";
import { TrendingUp, Medal } from "lucide-react";
import { Reveal } from "./Reveal";

const METRICS = [
  { value: 500, suffix: "+", label: "Podiums cumulés par le Bushido Club France depuis 2004", highlight: null, testid: "metric-podiums" },
  { value: 10, suffix: " ans", label: "Professeur en lycée professionnel", highlight: "Ingénieur pédagogique", testid: "metric-lycee" },
  { value: 15, suffix: " ans", label: "Éducateur sportif & arbitre national", highlight: "Exigence & discipline", testid: "metric-combat" },
  { value: 4, suffix: "e dan", label: "Ceinture noire de full contact", highlight: "Sur le tatami et sur le ring", testid: "metric-dan" },
];

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 1.8,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value]);

  return (
    <span ref={ref} data-testid={value === 500 ? "metric-podiums-value" : undefined}>
      {display}
      <span className="text-gold">{suffix}</span>
    </span>
  );
};

const Results = () => (
  <section id="qui-suis-je" className="relative py-24 sm:py-32 bg-coal overflow-hidden" data-testid="results-section">
    <div className="absolute inset-x-0 top-0 h-64 opacity-[0.07]">
      <img
        src="https://images.unsplash.com/photo-1552072092-7f9b8d63efcb?crop=entropy&cs=srgb&fm=jpg&q=85"
        alt=""
        className="w-full h-full object-cover"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-coal" />
    </div>

    <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
      <div className="grid gap-12 lg:grid-cols-[1.1fr_1fr] items-start">
        <Reveal>
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-ember mb-4">Qui suis-je</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-slate-100 leading-tight">
            Une approche dédiée : écouter, cadrer, faire progresser
          </h2>
          <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed">
            Je m'appelle Yassine Abou. Éducateur sportif depuis 15 ans et professeur en lycée
            professionnel, je suis aussi arbitre national au sein d'une fédération de sports
            de combat et ceinture noire 4e dan de full contact.
          </p>
          <p className="mt-4 text-sm sm:text-base text-slate-400 leading-relaxed">
            Au fil des années, j'ai porté plusieurs projets pédagogiques forts, notamment en
            lien avec l'Éducation nationale. J'accompagne aujourd'hui des ados et des
            étudiants qui ont besoin de reprendre confiance, de se structurer et de retrouver
            un cap. J'enseigne aussi le kickboxing au Bushido Club France — un club qui
            cumule plus de 500 podiums depuis 2004, un palmarès collectif dont je suis fier
            de faire partie.
          </p>
          <div className="mt-6 rounded-2xl border-l-4 border-ember bg-obsidian/60 p-5 sm:p-6">
            <p className="text-sm sm:text-base text-slate-200 leading-relaxed italic">
              « Préparateur physique et mental, éducateur sportif, enseignant et arbitre
              national : j'ai passé ma vie entre deux mondes qui s'ignorent trop souvent —
              la performance du corps et celle de l'esprit. Ma conviction tient en trois
              mots : un objectif, un plan clair, et s'y tenir. La méthode qui mène au podium
              est la même qui permet de décrocher un diplôme, un premier emploi ou une
              reconversion réussie. »
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-2 gap-4">
          {METRICS.map((m, i) => (
            <Reveal key={m.testid} delay={i * 0.1}>
              <div
                data-testid={m.testid === "metric-podiums" ? "metric-podiums-card" : m.testid}
                className="h-full rounded-2xl border border-white/10 bg-obsidian/70 backdrop-blur p-5 sm:p-6"
              >
                <div className="font-heading text-5xl sm:text-6xl font-black text-slate-100 leading-none">
                  <Counter value={m.value} suffix={m.suffix} />
                </div>
                <p className="mt-3 text-xs sm:text-sm text-slate-400 leading-snug">{m.label}</p>
                {m.highlight && (
                  <p className="mt-2 text-[11px] font-bold uppercase tracking-wide text-ember">{m.highlight}</p>
                )}
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.4} className="col-span-2">
            <div
              data-testid="metric-medailles"
              className="rounded-2xl border border-gold/30 bg-obsidian/70 backdrop-blur p-5 sm:p-6 flex items-center gap-4"
            >
              <span className="flex items-center justify-center w-12 h-12 rounded-2xl bg-gold/12 border border-gold/30 shrink-0">
                <Medal size={22} className="text-gold" />
              </span>
              <div>
                <p className="font-heading text-xl font-extrabold uppercase text-slate-100 leading-tight">Médaillé</p>
                <p className="mt-1 text-xs sm:text-sm text-slate-400 leading-snug">
                  Championnats de France & Coupe de France — à de nombreuses reprises
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      <div id="resultats" className="mt-24 scroll-mt-24">
        <Reveal>
          <div className="flex items-center gap-3 mb-3">
            <TrendingUp size={18} className="text-gold" />
            <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-gold">Résultats élèves</p>
          </div>
          <h3 className="font-heading text-2xl sm:text-3xl font-extrabold uppercase text-slate-100">
            Leurs réussites arrivent bientôt
          </h3>
        </Reveal>
        <Reveal delay={0.1}>
          <div
            data-testid="results-placeholder"
            className="mt-8 rounded-2xl border border-dashed border-white/15 bg-obsidian/50 p-8 sm:p-10 max-w-3xl"
          >
            <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
              Les parcours et résultats des élèves accompagnés seront publiés ici
              prochainement — avec l'accord écrit des familles et dans le respect de
              l'anonymat des mineurs.
            </p>
            <p className="mt-4 text-xs text-slate-500 leading-relaxed">
              Vous êtes parent d'un élève accompagné et souhaitez partager son parcours ?
              Mentionnez-le dans le formulaire de contact.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default Results;
